import { useCallback, useEffect, useState } from 'react';
import checkFeatureFlagByName from '../api/feature-flags/checkFeatureFlagByName';

/**
 * Flag que abre la aportación puntual. Se resuelve por `user_ids` en la tabla
 * `feature_flags`, así que se puede abrir a una sola persona.
 */
export const ONE_TIME_CONTRIBUTION_FEATURE_FLAG = 'one-time-contribution';

export interface UseOneTimeContributionEnabledResult {
	enabled: boolean;
	loading: boolean;
}

/**
 * ¿Puede esta persona hacer una aportación puntual?
 *
 * La aportación puntual mueve DINERO REAL del cliente a Caser y es irreversible
 * desde la app: deshacer una equivocada es una gestión manual de Operaciones con
 * Caser. Por eso se abre persona a persona hasta haberla visto funcionar de
 * punta a punta en PROD con un importe pequeño.
 *
 * **Ante cualquier duda, apagada**: sin token, sin usuario, con la petición
 * fallando o con el flag sin crear (el backend devuelve `isActive: false` si no
 * existe), no se ofrece el botón.
 *
 * Ojo: esto es un gate de FRONT. `POST /users/one-time-contribution` NO
 * comprueba el flag — quien conozca la ruta puede llamarlo igual.
 */
const useOneTimeContributionEnabled = (
	token: string | null | undefined,
	companyId: string | null | undefined,
	userId: string | null | undefined
): UseOneTimeContributionEnabledResult => {
	const [enabled, setEnabled] = useState(false);
	const [loading, setLoading] = useState(false);

	const check = useCallback(async () => {
		if (!token || !userId) {
			setEnabled(false);
			return;
		}

		setLoading(true);

		try {
			const res = await checkFeatureFlagByName(
				token,
				companyId ?? '',
				userId,
				ONE_TIME_CONTRIBUTION_FEATURE_FLAG
			);
			setEnabled(!!(res?.success && res.data?.isActive === true));
		} catch {
			// Un fallo de red no puede abrir a todo el mundo una operación que
			// mueve dinero.
			setEnabled(false);
		} finally {
			setLoading(false);
		}
	}, [token, companyId, userId]);

	useEffect(() => {
		check();
	}, [check]);

	return { enabled, loading };
};

export default useOneTimeContributionEnabled;
